import { useEffect, useState } from "react";
import { Box, Group, Stack, Text, useMantineColorScheme } from "@mantine/core";
import {
	IconArrowDown,
	IconBell,
	IconChevronRight,
	IconHandFinger,
	IconMapPin,
	IconMicrophone,
	IconRefresh,
	IconRotate,
	IconSearch,
} from "@tabler/icons-react";

export type SettingsWalkthroughProps = {
	os: "ios" | "android";
	permission: "notifications" | "location" | "microphone";
};

export function SettingsWalkthrough({
	os,
	permission,
}: SettingsWalkthroughProps) {
	if (os === "ios") {
		if (permission === "notifications") {
			return <IOSNotificationsWalkthrough />;
		}
		if (permission === "microphone") {
			return <IOSMicrophoneWalkthrough />;
		}
		return <IOSLocationWalkthrough />;
	}
	if (permission === "notifications") {
		return <AndroidNotificationsWalkthrough />;
	}
	if (permission === "microphone") {
		return <AndroidMicrophoneWalkthrough />;
	}
	return <AndroidLocationWalkthrough />;
}

function useStepCycle(stepCount: number, intervalMs = 2500) {
	const [activeStep, setActiveStep] = useState(0);

	useEffect(() => {
		const timer = setInterval(() => {
			setActiveStep((s) => (s + 1) % stepCount);
		}, intervalMs);
		return () => clearInterval(timer);
	}, [stepCount, intervalMs]);

	const rewind = () => setActiveStep(0);

	return [activeStep, rewind] as const;
}

const walkthroughKeyframes = `
@keyframes settingsHighlight {
	0%, 100% { opacity: 0.6; }
	50% { opacity: 1; }
}
@keyframes tapPointerPulse {
	0%, 100% { transform: scale(1) translateY(0); opacity: 0.9; }
	50% { transform: scale(1.1) translateY(-2px); opacity: 0.7; }
}
`;

function TapPointer({
	top,
	left,
	right,
}: { top: number; left?: number; right?: number }) {
	const { colorScheme } = useMantineColorScheme();
	const isDark = colorScheme === "dark";

	return (
		<Box
			style={{
				position: "absolute",
				top,
				left,
				right,
				animation: "tapPointerPulse 1.5s ease-in-out infinite",
				pointerEvents: "none",
				zIndex: 10,
			}}
		>
			<IconHandFinger
				size={20}
				color={isDark ? "white" : "black"}
				fill={isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)"}
				style={{
					filter: isDark
						? "drop-shadow(0 1px 3px rgba(0,0,0,0.5))"
						: "drop-shadow(0 1px 2px rgba(0,0,0,0.2))",
				}}
			/>
		</Box>
	);
}

function StepDots({ count, active }: { count: number; active: number }) {
	const keys = Array.from({ length: count }, (_, i) => `step-dot-${i}`);

	return (
		<Group gap={6} justify="center">
			{keys.map((key, i) => (
				<Box
					key={key}
					style={{
						width: i === active ? 16 : 6,
						height: 6,
						borderRadius: 3,
						backgroundColor:
							i === active
								? "var(--mantine-color-text)"
								: "var(--mantine-color-default-border)",
						transition: "width 200ms ease, background-color 200ms ease",
					}}
				/>
			))}
		</Group>
	);
}

function WalkthroughFrame({
	visible,
	children,
}: {
	visible: boolean;
	children: React.ReactNode;
}) {
	return (
		<Box
			style={{
				position: "absolute",
				inset: 0,
				opacity: visible ? 1 : 0,
				transition: "opacity 400ms ease",
				pointerEvents: visible ? "auto" : "none",
			}}
		>
			{children}
		</Box>
	);
}

function WalkthroughContainer({
	stepCount,
	activeStep,
	onRewind,
	children,
}: {
	stepCount: number;
	activeStep: number;
	onRewind: () => void;
	children: React.ReactNode;
}) {
	const { colorScheme } = useMantineColorScheme();
	const isDark = colorScheme === "dark";

	return (
		<Box
			style={{
				position: "relative",
				height: 240,
				borderRadius: 12,
				overflow: "hidden",
				backgroundColor: isDark
					? "var(--mantine-color-dark-7)"
					: "var(--mantine-color-gray-1)",
				border: `1px solid ${isDark ? "var(--mantine-color-dark-4)" : "var(--mantine-color-gray-3)"}`,
			}}
		>
			<style>{walkthroughKeyframes}</style>
			{children}
			<Group
				gap={8}
				align="center"
				style={{
					position: "absolute",
					bottom: 8,
					left: "50%",
					transform: "translateX(-50%)",
					backgroundColor: isDark
						? "var(--mantine-color-dark-6)"
						: "var(--mantine-color-gray-2)",
					borderRadius: 999,
					padding: "4px 10px",
				}}
			>
				<StepDots count={stepCount} active={activeStep} />
				<Box
					onClick={onRewind}
					style={{
						cursor: "pointer",
						width: 24,
						height: 24,
						borderRadius: "50%",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						backgroundColor: isDark
							? "var(--mantine-color-dark-4)"
							: "var(--mantine-color-gray-3)",
					}}
				>
					<IconRotate
						size={14}
						color={
							isDark
								? "var(--mantine-color-gray-4)"
								: "var(--mantine-color-gray-7)"
						}
					/>
				</Box>
			</Group>
		</Box>
	);
}

// iOS shared primitives

function IOSBackToAppBar({ highlighted }: { highlighted?: boolean }) {
	const { colorScheme } = useMantineColorScheme();
	const isDark = colorScheme === "dark";

	return (
		<Box
			px="sm"
			py={3}
			style={{
				borderBottom: `1px solid ${isDark ? "var(--mantine-color-dark-5)" : "var(--mantine-color-gray-2)"}`,
				backgroundColor: highlighted
					? isDark
						? "rgba(59,130,246,0.15)"
						: "rgba(59,130,246,0.08)"
					: "transparent",
			}}
		>
			<Text fz={9} c="blue" fw={400}>
				&#9664; Village Caregiving
			</Text>
		</Box>
	);
}

function IOSNavBar({
	title,
	backLabel,
}: { title: string; backLabel?: string }) {
	const { colorScheme } = useMantineColorScheme();
	const isDark = colorScheme === "dark";

	return (
		<Group
			justify="space-between"
			px="sm"
			py={6}
			style={{
				borderBottom: `1px solid ${isDark ? "var(--mantine-color-dark-4)" : "var(--mantine-color-gray-3)"}`,
			}}
		>
			<Text size="xs" c="blue" fw={400}>
				{backLabel || "Back"}
			</Text>
			<Text size="xs" fw={600} c={isDark ? "white" : "dark"}>
				{title}
			</Text>
			<Box style={{ width: 30 }} />
		</Group>
	);
}

function IOSSettingsRow({
	icon,
	iconBg,
	label,
	value,
	highlighted,
}: {
	icon: React.ReactNode;
	iconBg: string;
	label: string;
	value?: string;
	highlighted?: boolean;
}) {
	const { colorScheme } = useMantineColorScheme();
	const isDark = colorScheme === "dark";

	return (
		<Group
			wrap="nowrap"
			gap="xs"
			px="sm"
			py={6}
			style={{
				backgroundColor: highlighted
					? isDark
						? "rgba(59,130,246,0.15)"
						: "rgba(59,130,246,0.08)"
					: "transparent",
				borderRadius: 4,
			}}
		>
			<Box
				style={{
					width: 20,
					height: 20,
					borderRadius: 5,
					backgroundColor: iconBg,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					flexShrink: 0,
				}}
			>
				{icon}
			</Box>
			<Text fz={11} fw={400} c={isDark ? "white" : "dark"} style={{ flex: 1 }}>
				{label}
			</Text>
			{value && (
				<Text fz={11} c="dimmed">
					{value}
				</Text>
			)}
			<IconChevronRight size={10} color="var(--mantine-color-dimmed)" />
		</Group>
	);
}

function IOSSettingsToggleRow({
	icon,
	iconBg,
	label,
	on,
}: {
	icon: React.ReactNode;
	iconBg: string;
	label: string;
	on: boolean;
}) {
	const { colorScheme } = useMantineColorScheme();
	const isDark = colorScheme === "dark";

	return (
		<Group wrap="nowrap" gap="xs" px="sm" py={6} style={{ borderRadius: 4 }}>
			<Box
				style={{
					width: 20,
					height: 20,
					borderRadius: 5,
					backgroundColor: iconBg,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					flexShrink: 0,
				}}
			>
				{icon}
			</Box>
			<Text fz={11} fw={400} c={isDark ? "white" : "dark"} style={{ flex: 1 }}>
				{label}
			</Text>
			<IOSToggle on={on} />
		</Group>
	);
}

function IOSToggleRow({
	label,
	on,
	highlighted,
}: {
	label: string;
	on: boolean;
	highlighted?: boolean;
}) {
	const { colorScheme } = useMantineColorScheme();
	const isDark = colorScheme === "dark";

	return (
		<Group
			wrap="nowrap"
			justify="space-between"
			px="sm"
			py={6}
			style={{
				backgroundColor: highlighted
					? isDark
						? "rgba(59,130,246,0.15)"
						: "rgba(59,130,246,0.08)"
					: isDark
						? "var(--mantine-color-dark-6)"
						: "white",
				borderRadius: 6,
				margin: "0 8px",
			}}
		>
			<Text fz={11} fw={400} c={isDark ? "white" : "dark"}>
				{label}
			</Text>
			<IOSToggle on={on} />
		</Group>
	);
}

function IOSToggle({ on }: { on: boolean }) {
	return (
		<Box
			style={{
				width: 34,
				height: 18,
				borderRadius: 9,
				backgroundColor: on ? "#34c759" : "#787880",
				position: "relative",
				transition: "background-color 300ms ease",
			}}
		>
			<Box
				style={{
					width: 14,
					height: 14,
					borderRadius: 7,
					backgroundColor: "white",
					position: "absolute",
					top: 2,
					left: on ? 18 : 2,
					transition: "left 300ms ease",
					boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
				}}
			/>
		</Box>
	);
}

function IOSRadioRow({
	label,
	selected,
	highlighted,
}: {
	label: string;
	selected: boolean;
	highlighted?: boolean;
}) {
	const { colorScheme } = useMantineColorScheme();
	const isDark = colorScheme === "dark";

	return (
		<Group
			wrap="nowrap"
			justify="space-between"
			px="sm"
			py={5}
			style={{
				backgroundColor: highlighted
					? isDark
						? "rgba(59,130,246,0.15)"
						: "rgba(59,130,246,0.08)"
					: "transparent",
				borderBottom: `1px solid ${isDark ? "var(--mantine-color-dark-4)" : "var(--mantine-color-gray-2)"}`,
			}}
		>
			<Text fz={11} c={isDark ? "white" : "dark"}>
				{label}
			</Text>
			{selected && (
				<Text fz={11} c="blue" fw={600}>
					&#10003;
				</Text>
			)}
		</Group>
	);
}

// Android shared primitives

function AndroidNavBar({ title }: { title: string }) {
	const { colorScheme } = useMantineColorScheme();
	const isDark = colorScheme === "dark";

	return (
		<Group gap="xs" px="sm" py={8}>
			<Text size="xs" c="dimmed">
				&#8592;
			</Text>
			<Text size="xs" fw={500} c={isDark ? "white" : "dark"}>
				{title}
			</Text>
		</Group>
	);
}

function AndroidNavBarHighlighted({ title }: { title: string }) {
	const { colorScheme } = useMantineColorScheme();
	const isDark = colorScheme === "dark";

	return (
		<Group gap="xs" px="sm" py={8}>
			<Box
				style={{
					backgroundColor: isDark
						? "rgba(129,140,248,0.25)"
						: "rgba(99,102,241,0.12)",
					borderRadius: 12,
					padding: "2px 6px",
				}}
			>
				<Text
					size="xs"
					c={
						isDark
							? "var(--mantine-color-indigo-3)"
							: "var(--mantine-color-indigo-6)"
					}
					fw={500}
				>
					&#8592; Back
				</Text>
			</Box>
			<Text size="xs" fw={500} c={isDark ? "white" : "dark"}>
				{title}
			</Text>
		</Group>
	);
}

function AndroidSettingsRow({
	icon,
	label,
	subtitle,
	highlighted,
}: {
	icon?: React.ReactNode;
	label: string;
	subtitle?: string;
	highlighted?: boolean;
}) {
	const { colorScheme } = useMantineColorScheme();
	const isDark = colorScheme === "dark";

	return (
		<Group
			wrap="nowrap"
			gap="xs"
			px="sm"
			py={6}
			style={{
				backgroundColor: highlighted
					? isDark
						? "rgba(129,140,248,0.15)"
						: "rgba(99,102,241,0.08)"
					: "transparent",
				borderRadius: 8,
				margin: "0 6px",
			}}
		>
			{icon && <Box style={{ flexShrink: 0, opacity: 0.7 }}>{icon}</Box>}
			<Stack gap={0}>
				<Text fz={11} fw={400} c={isDark ? "white" : "dark"}>
					{label}
				</Text>
				{subtitle && (
					<Text fz={9} c="dimmed">
						{subtitle}
					</Text>
				)}
			</Stack>
		</Group>
	);
}

function AndroidToggle({ on }: { on: boolean }) {
	const { colorScheme } = useMantineColorScheme();
	const isDark = colorScheme === "dark";

	return (
		<Box
			style={{
				width: 34,
				height: 18,
				borderRadius: 9,
				backgroundColor: on
					? isDark
						? "var(--mantine-color-indigo-4)"
						: "var(--mantine-color-indigo-6)"
					: isDark
						? "var(--mantine-color-dark-3)"
						: "var(--mantine-color-gray-4)",
				position: "relative",
				transition: "background-color 300ms ease",
			}}
		>
			<Box
				style={{
					width: 14,
					height: 14,
					borderRadius: 7,
					backgroundColor: on
						? "white"
						: isDark
							? "var(--mantine-color-dark-1)"
							: "white",
					position: "absolute",
					top: 2,
					left: on ? 18 : 2,
					transition: "left 300ms ease",
					boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
				}}
			/>
		</Box>
	);
}

function AndroidToggleRow({
	label,
	subtitle,
	on,
	highlighted,
}: {
	label: string;
	subtitle?: string;
	on: boolean;
	highlighted?: boolean;
}) {
	const { colorScheme } = useMantineColorScheme();
	const isDark = colorScheme === "dark";

	return (
		<Group
			wrap="nowrap"
			justify="space-between"
			px="sm"
			py={6}
			style={{
				backgroundColor: highlighted
					? isDark
						? "rgba(129,140,248,0.15)"
						: "rgba(99,102,241,0.08)"
					: isDark
						? "var(--mantine-color-dark-6)"
						: "var(--mantine-color-gray-0)",
				borderRadius: 16,
				margin: "0 6px",
			}}
		>
			<Stack gap={0} style={{ flex: 1 }}>
				<Text fz={11} fw={400} c={isDark ? "white" : "dark"}>
					{label}
				</Text>
				{subtitle && (
					<Text fz={9} c="dimmed">
						{subtitle}
					</Text>
				)}
			</Stack>
			<AndroidToggle on={on} />
		</Group>
	);
}

function AndroidRadioRow({
	label,
	selected,
	highlighted,
}: {
	label: string;
	selected: boolean;
	highlighted?: boolean;
}) {
	const { colorScheme } = useMantineColorScheme();
	const isDark = colorScheme === "dark";

	return (
		<Group
			wrap="nowrap"
			gap="xs"
			px="sm"
			py={5}
			style={{
				backgroundColor: highlighted
					? isDark
						? "rgba(129,140,248,0.15)"
						: "rgba(99,102,241,0.08)"
					: isDark
						? "var(--mantine-color-dark-6)"
						: "var(--mantine-color-gray-0)",
				borderRadius: 8,
				margin: "2px 6px",
			}}
		>
			<Box
				style={{
					width: 14,
					height: 14,
					borderRadius: 7,
					border: `2px solid ${
						selected
							? isDark
								? "var(--mantine-color-indigo-4)"
								: "var(--mantine-color-indigo-6)"
							: isDark
								? "var(--mantine-color-dark-3)"
								: "var(--mantine-color-gray-4)"
					}`,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					flexShrink: 0,
				}}
			>
				{selected && (
					<Box
						style={{
							width: 6,
							height: 6,
							borderRadius: 3,
							backgroundColor: isDark
								? "var(--mantine-color-indigo-4)"
								: "var(--mantine-color-indigo-6)",
						}}
					/>
				)}
			</Box>
			<Text fz={11} c={isDark ? "white" : "dark"}>
				{label}
			</Text>
		</Group>
	);
}

// Walkthrough implementations

function IOSNotificationsWalkthrough() {
	const [step, rewind] = useStepCycle(5);

	return (
		<WalkthroughContainer stepCount={5} activeStep={step} onRewind={rewind}>
			{/* Step 1: App settings home, pointer on Notifications row */}
			<WalkthroughFrame visible={step === 0}>
				<IOSBackToAppBar />
				<IOSNavBar title="Village Caregiving" backLabel="Apps" />
				<Text fz={9} c="dimmed" px="sm" pt={4} pb={1} tt="uppercase">
					Allow Village Caregiving to Access
				</Text>
				<IOSSettingsRow
					icon={<IconMapPin size={11} color="white" />}
					iconBg="#007AFF"
					label="Location"
					value="Never"
				/>
				<IOSSettingsRow
					icon={
						<Box
							style={{
								width: 11,
								height: 11,
								borderRadius: "50%",
								background:
									"radial-gradient(circle, #ff6b9d, #c44dff, #4dabff)",
							}}
						/>
					}
					iconBg="#000"
					label="Siri"
				/>
				<IOSSettingsRow
					icon={<IconSearch size={11} color="white" />}
					iconBg="#8E8E93"
					label="Search"
				/>
				<IOSSettingsRow
					icon={<IconBell size={11} color="white" />}
					iconBg="#FF3B30"
					label="Notifications"
					value="Off"
					highlighted
				/>
				<IOSSettingsToggleRow
					icon={<IconRefresh size={11} color="white" />}
					iconBg="#5856D6"
					label="Background App Refresh"
					on={true}
				/>
				<TapPointer top={163} right={40} />
			</WalkthroughFrame>

			{/* Step 2: Notifications screen, toggle OFF, pointer on toggle */}
			<WalkthroughFrame visible={step === 1}>
				<IOSBackToAppBar />
				<IOSNavBar title="Notifications" backLabel="Back" />
				<Box py={8}>
					<IOSToggleRow label="Allow Notifications" on={false} highlighted />
				</Box>
				<TapPointer top={72} right={22} />
			</WalkthroughFrame>

			{/* Step 3: Notifications screen, toggle ON */}
			<WalkthroughFrame visible={step === 2}>
				<IOSBackToAppBar />
				<IOSNavBar title="Notifications" backLabel="Back" />
				<Box py={8}>
					<IOSToggleRow label="Allow Notifications" on={true} highlighted />
				</Box>
				<Text fz={9} c="dimmed" px="sm" pt={4} pb={1} tt="uppercase">
					Alerts
				</Text>
				<Group gap="xs" justify="center" py={2}>
					<Text fz={8} c="dimmed" ta="center">
						Lock Screen
					</Text>
					<Text fz={8} c="dimmed" ta="center">
						Notification Center
					</Text>
					<Text fz={8} c="dimmed" ta="center">
						Banners
					</Text>
				</Group>
			</WalkthroughFrame>

			{/* Step 4: Back to app - highlight the breadcrumb */}
			<WalkthroughFrame visible={step === 3}>
				<IOSBackToAppBar highlighted />
				<IOSNavBar title="Notifications" backLabel="Back" />
				<Box py={8}>
					<IOSToggleRow label="Allow Notifications" on={true} />
				</Box>
				<Text fz={9} c="dimmed" px="sm" pt={4} pb={1} tt="uppercase">
					Alerts
				</Text>
				<Group gap="xs" justify="center" py={2}>
					<Text fz={8} c="dimmed" ta="center">
						Lock Screen
					</Text>
					<Text fz={8} c="dimmed" ta="center">
						Notification Center
					</Text>
					<Text fz={8} c="dimmed" ta="center">
						Banners
					</Text>
				</Group>
				<TapPointer top={1} left={55} />
			</WalkthroughFrame>

			{/* Step 5: Tap Open Settings below */}
			<WalkthroughFrame visible={step === 4}>
				<TapOpenSettingsStep />
			</WalkthroughFrame>
		</WalkthroughContainer>
	);
}

function IOSLocationWalkthrough() {
	const [step, rewind] = useStepCycle(7);

	return (
		<WalkthroughContainer stepCount={7} activeStep={step} onRewind={rewind}>
			{/* Step 1: App settings home, pointer on Location row */}
			<WalkthroughFrame visible={step === 0}>
				<IOSBackToAppBar />
				<IOSNavBar title="Village Caregiving" backLabel="Apps" />
				<Text fz={9} c="dimmed" px="sm" pt={4} pb={1} tt="uppercase">
					Allow Village Caregiving to Access
				</Text>
				<IOSSettingsRow
					icon={<IconMapPin size={11} color="white" />}
					iconBg="#007AFF"
					label="Location"
					value="Never"
					highlighted
				/>
				<IOSSettingsRow
					icon={
						<Box
							style={{
								width: 11,
								height: 11,
								borderRadius: "50%",
								background:
									"radial-gradient(circle, #ff6b9d, #c44dff, #4dabff)",
							}}
						/>
					}
					iconBg="#000"
					label="Siri"
				/>
				<IOSSettingsRow
					icon={<IconSearch size={11} color="white" />}
					iconBg="#8E8E93"
					label="Search"
				/>
				<IOSSettingsRow
					icon={<IconBell size={11} color="white" />}
					iconBg="#FF3B30"
					label="Notifications"
					value="Off"
				/>
				<IOSSettingsToggleRow
					icon={<IconRefresh size={11} color="white" />}
					iconBg="#5856D6"
					label="Background App Refresh"
					on={true}
				/>
				<TapPointer top={88} right={40} />
			</WalkthroughFrame>

			{/* Step 2: Location screen, "Never" selected, pointer on "While Using the App" */}
			<WalkthroughFrame visible={step === 1}>
				<IOSBackToAppBar />
				<IOSNavBar title="Location" backLabel="Back" />
				<Text fz={9} c="dimmed" px="sm" pt={4} pb={1} tt="uppercase">
					Allow Location Access
				</Text>
				<IOSRadioRow label="Never" selected={true} />
				<IOSRadioRow label="Ask Next Time Or When I Share" selected={false} />
				<IOSRadioRow label="While Using the App" selected={false} highlighted />
				<IOSRadioRow label="Always" selected={false} />
				<TapPointer top={132} right={30} />
			</WalkthroughFrame>

			{/* Step 3: Location screen, "While Using the App" now selected */}
			<WalkthroughFrame visible={step === 2}>
				<IOSBackToAppBar />
				<IOSNavBar title="Location" backLabel="Back" />
				<Text fz={9} c="dimmed" px="sm" pt={4} pb={1} tt="uppercase">
					Allow Location Access
				</Text>
				<IOSRadioRow label="Never" selected={false} />
				<IOSRadioRow label="Ask Next Time Or When I Share" selected={false} />
				<IOSRadioRow label="While Using the App" selected={true} highlighted />
				<IOSRadioRow label="Always" selected={false} />
				<Text fz={8} c="dimmed" px="sm" pt={4} style={{ fontStyle: "italic" }}>
					App explanation: &ldquo;electronic verification of visits and
					signatures&rdquo;
				</Text>
			</WalkthroughFrame>

			{/* Step 4: Precise Location toggle OFF, pointer on toggle */}
			<WalkthroughFrame visible={step === 3}>
				<IOSBackToAppBar />
				<IOSNavBar title="Location" backLabel="Back" />
				<Text fz={9} c="dimmed" px="sm" pt={4} pb={1} tt="uppercase">
					Allow Location Access
				</Text>
				<IOSRadioRow label="While Using the App" selected={true} />
				<Text fz={8} c="dimmed" px="sm" pt={4} style={{ fontStyle: "italic" }}>
					App explanation: &ldquo;electronic verification of visits and
					signatures&rdquo;
				</Text>
				<Box pt={4}>
					<IOSToggleRow label="Precise Location" on={false} highlighted />
				</Box>
				<TapPointer top={155} right={22} />
			</WalkthroughFrame>

			{/* Step 5: Precise Location toggle ON */}
			<WalkthroughFrame visible={step === 4}>
				<IOSBackToAppBar />
				<IOSNavBar title="Location" backLabel="Back" />
				<Text fz={9} c="dimmed" px="sm" pt={4} pb={1} tt="uppercase">
					Allow Location Access
				</Text>
				<IOSRadioRow label="While Using the App" selected={true} />
				<Text fz={8} c="dimmed" px="sm" pt={4} style={{ fontStyle: "italic" }}>
					App explanation: &ldquo;electronic verification of visits and
					signatures&rdquo;
				</Text>
				<Box pt={4}>
					<IOSToggleRow label="Precise Location" on={true} highlighted />
				</Box>
			</WalkthroughFrame>

			{/* Step 6: Back to app */}
			<WalkthroughFrame visible={step === 5}>
				<IOSBackToAppBar highlighted />
				<IOSNavBar title="Location" backLabel="Back" />
				<Text fz={9} c="dimmed" px="sm" pt={4} pb={1} tt="uppercase">
					Allow Location Access
				</Text>
				<IOSRadioRow label="While Using the App" selected={true} />
				<Text fz={8} c="dimmed" px="sm" pt={4} style={{ fontStyle: "italic" }}>
					App explanation: &ldquo;electronic verification of visits and
					signatures&rdquo;
				</Text>
				<Box pt={4}>
					<IOSToggleRow label="Precise Location" on={true} />
				</Box>
				<TapPointer top={1} left={55} />
			</WalkthroughFrame>

			{/* Step 7: Tap Open Settings below */}
			<WalkthroughFrame visible={step === 6}>
				<TapOpenSettingsStep />
			</WalkthroughFrame>
		</WalkthroughContainer>
	);
}

function AndroidNotificationsWalkthrough() {
	const [step, rewind] = useStepCycle(5);

	return (
		<WalkthroughContainer stepCount={5} activeStep={step} onRewind={rewind}>
			{/* Step 1: App info home, pointer on Notifications row */}
			<WalkthroughFrame visible={step === 0}>
				<AndroidNavBar title="App info" />
				<Stack gap={2} align="center" py={2}>
					<VCGLogo size={24} />
					<Text fz={11} fw={500} ta="center">
						Village Caregiving
					</Text>
				</Stack>
				<AndroidSettingsRow label="Notifications" subtitle="Off" highlighted />
				<AndroidSettingsRow
					label="Permissions"
					subtitle="No permissions granted"
				/>
				<AndroidSettingsRow label="Storage & cache" subtitle="35.77 MB used" />
				<AndroidSettingsRow label="Mobile data usage" subtitle="115 kB used" />
				<TapPointer top={88} right={30} />
			</WalkthroughFrame>

			{/* Step 2: Notifications screen, toggle OFF, pointer on toggle */}
			<WalkthroughFrame visible={step === 1}>
				<AndroidNavBar title="Village Caregiving" />
				<Stack gap="xs" align="center" py={6}>
					<VCGLogo size={24} />
				</Stack>
				<AndroidToggleRow
					label="All Village Caregiving notifications"
					on={false}
					highlighted
				/>
				<Box px="sm" py={4}>
					<Text fz={9} c="dimmed">
						You haven't allowed notifications from this app
					</Text>
				</Box>
				<TapPointer top={90} right={18} />
			</WalkthroughFrame>

			{/* Step 3: Notifications screen, toggle ON */}
			<WalkthroughFrame visible={step === 2}>
				<AndroidNavBar title="Village Caregiving" />
				<Stack gap="xs" align="center" py={6}>
					<VCGLogo size={24} />
				</Stack>
				<AndroidToggleRow
					label="All Village Caregiving notifications"
					on={true}
					highlighted
				/>
				<Box px="sm" py={4}>
					<Text fz={9} c="dimmed">
						Notification categories
					</Text>
				</Box>
			</WalkthroughFrame>

			{/* Step 4: Back to app */}
			<WalkthroughFrame visible={step === 3}>
				<AndroidNavBarHighlighted title="Village Caregiving" />
				<Stack gap="xs" align="center" py={6}>
					<VCGLogo size={24} />
				</Stack>
				<AndroidToggleRow
					label="All Village Caregiving notifications"
					on={true}
				/>
				<Box px="sm" py={4}>
					<Text fz={9} c="dimmed">
						Notification categories
					</Text>
				</Box>
				<TapPointer top={5} left={16} />
			</WalkthroughFrame>

			{/* Step 5: Tap Open Settings below */}
			<WalkthroughFrame visible={step === 4}>
				<TapOpenSettingsStep />
			</WalkthroughFrame>
		</WalkthroughContainer>
	);
}

function AndroidLocationWalkthrough() {
	const [step, rewind] = useStepCycle(7);

	return (
		<WalkthroughContainer stepCount={7} activeStep={step} onRewind={rewind}>
			{/* Step 1: App info home, pointer on Permissions row */}
			<WalkthroughFrame visible={step === 0}>
				<AndroidNavBar title="App info" />
				<Stack gap={2} align="center" py={2}>
					<VCGLogo size={24} />
					<Text fz={11} fw={500} ta="center">
						Village Caregiving
					</Text>
				</Stack>
				<AndroidSettingsRow label="Notifications" subtitle="Off" />
				<AndroidSettingsRow
					label="Permissions"
					subtitle="No permissions granted"
					highlighted
				/>
				<AndroidSettingsRow label="Storage & cache" subtitle="35.77 MB used" />
				<AndroidSettingsRow label="Mobile data usage" subtitle="115 kB used" />
				<TapPointer top={118} right={30} />
			</WalkthroughFrame>

			{/* Step 2: Permissions screen, pointer on Location */}
			<WalkthroughFrame visible={step === 1}>
				<AndroidNavBar title="App permissions" />
				<Stack gap={2} align="center" py={2}>
					<VCGLogo size={20} />
				</Stack>
				<Text fz={9} c="dimmed" px="sm" pb={1}>
					Not allowed
				</Text>
				<AndroidSettingsRow
					icon={<IconMapPin size={13} color="var(--mantine-color-dimmed)" />}
					label="Location"
					highlighted
				/>
				<AndroidSettingsRow
					icon={<IconBell size={13} color="var(--mantine-color-dimmed)" />}
					label="Notifications"
				/>
				<TapPointer top={96} right={30} />
			</WalkthroughFrame>

			{/* Step 3: Location permission, "Don't allow" selected, pointer on "Allow only while using" */}
			<WalkthroughFrame visible={step === 2}>
				<AndroidNavBar title="Location permission" />
				<Text fz={9} c="dimmed" px="sm" pt={2} pb={1}>
					Location access for this app
				</Text>
				<AndroidRadioRow
					label="Allow only while using the app"
					selected={false}
					highlighted
				/>
				<AndroidRadioRow label="Ask every time" selected={false} />
				<AndroidRadioRow label="Don't allow" selected={true} />
				<TapPointer top={62} right={30} />
			</WalkthroughFrame>

			{/* Step 4: "Allow only while using" now selected, precise toggle OFF */}
			<WalkthroughFrame visible={step === 3}>
				<AndroidNavBar title="Location permission" />
				<Text fz={9} c="dimmed" px="sm" pt={2} pb={1}>
					Location access for this app
				</Text>
				<AndroidRadioRow
					label="Allow only while using the app"
					selected={true}
					highlighted
				/>
				<AndroidRadioRow label="Ask every time" selected={false} />
				<AndroidRadioRow label="Don't allow" selected={false} />
				<Box pt={4}>
					<AndroidToggleRow
						label="Use precise location"
						subtitle="When off, apps can only get approximate location"
						on={false}
						highlighted
					/>
				</Box>
				<TapPointer top={155} right={18} />
			</WalkthroughFrame>

			{/* Step 5: Precise location ON */}
			<WalkthroughFrame visible={step === 4}>
				<AndroidNavBar title="Location permission" />
				<Text fz={9} c="dimmed" px="sm" pt={2} pb={1}>
					Location access for this app
				</Text>
				<AndroidRadioRow
					label="Allow only while using the app"
					selected={true}
				/>
				<AndroidRadioRow label="Ask every time" selected={false} />
				<AndroidRadioRow label="Don't allow" selected={false} />
				<Box pt={4}>
					<AndroidToggleRow
						label="Use precise location"
						subtitle="When off, apps can only get approximate location"
						on={true}
						highlighted
					/>
				</Box>
			</WalkthroughFrame>

			{/* Step 6: Back to app */}
			<WalkthroughFrame visible={step === 5}>
				<AndroidNavBarHighlighted title="Location permission" />
				<Text fz={9} c="dimmed" px="sm" pt={2} pb={1}>
					Location access for this app
				</Text>
				<AndroidRadioRow
					label="Allow only while using the app"
					selected={true}
				/>
				<AndroidRadioRow label="Ask every time" selected={false} />
				<AndroidRadioRow label="Don't allow" selected={false} />
				<Box pt={4}>
					<AndroidToggleRow
						label="Use precise location"
						subtitle="When off, apps can only get approximate location"
						on={true}
					/>
				</Box>
				<TapPointer top={5} left={16} />
			</WalkthroughFrame>

			{/* Step 7: Tap Open Settings below */}
			<WalkthroughFrame visible={step === 6}>
				<TapOpenSettingsStep />
			</WalkthroughFrame>
		</WalkthroughContainer>
	);
}

function TapOpenSettingsStep() {
	const { colorScheme } = useMantineColorScheme();
	const isDark = colorScheme === "dark";

	return (
		<Stack align="center" justify="center" gap="sm" h="100%" px="md">
			<Text fz="sm" fw={600} ta="center" c={isDark ? "white" : "dark"}>
				Now tap Open Settings below
			</Text>
			<Box
				style={{
					animation: "tapPointerPulse 1.5s ease-in-out infinite",
				}}
			>
				<IconArrowDown
					size={28}
					color={
						isDark
							? "var(--mantine-color-blue-4)"
							: "var(--mantine-color-blue-6)"
					}
				/>
			</Box>
		</Stack>
	);
}

function VCGLogo({ size = 28 }: { size?: number }) {
	return (
		<img
			src="/logo192.png"
			alt="Village Caregiving"
			style={{
				width: size,
				height: size,
				borderRadius: 6,
			}}
		/>
	);
}

function IOSMicrophoneWalkthrough() {
	const [step, rewind] = useStepCycle(4);

	return (
		<WalkthroughContainer stepCount={4} activeStep={step} onRewind={rewind}>
			{/* Step 1: App settings home, pointer on Microphone row */}
			<WalkthroughFrame visible={step === 0}>
				<IOSBackToAppBar />
				<IOSNavBar title="Village Caregiving" backLabel="Apps" />
				<Text fz={9} c="dimmed" px="sm" pt={4} pb={1} tt="uppercase">
					Allow Village Caregiving to Access
				</Text>
				<IOSSettingsRow
					icon={<IconMapPin size={11} color="white" />}
					iconBg="#007AFF"
					label="Location"
					value="While Using"
				/>
				<IOSSettingsRow
					icon={<IconMicrophone size={11} color="white" />}
					iconBg="#FF9500"
					label="Microphone"
					highlighted
				/>
				<IOSSettingsRow
					icon={<IconBell size={11} color="white" />}
					iconBg="#FF3B30"
					label="Notifications"
					value="On"
				/>
				<TapPointer top={115} right={40} />
			</WalkthroughFrame>

			{/* Step 2: Microphone toggle OFF, pointer on toggle */}
			<WalkthroughFrame visible={step === 1}>
				<IOSBackToAppBar />
				<IOSNavBar title="Village Caregiving" backLabel="Back" />
				<Box py={8}>
					<IOSToggleRow label="Microphone" on={false} highlighted />
				</Box>
				<TapPointer top={72} right={22} />
			</WalkthroughFrame>

			{/* Step 3: Microphone toggle ON */}
			<WalkthroughFrame visible={step === 2}>
				<IOSBackToAppBar />
				<IOSNavBar title="Village Caregiving" backLabel="Back" />
				<Box py={8}>
					<IOSToggleRow label="Microphone" on={true} highlighted />
				</Box>
			</WalkthroughFrame>

			{/* Step 4: Tap Open Settings below */}
			<WalkthroughFrame visible={step === 3}>
				<TapOpenSettingsStep />
			</WalkthroughFrame>
		</WalkthroughContainer>
	);
}

function AndroidMicrophoneWalkthrough() {
	const [step, rewind] = useStepCycle(5);

	return (
		<WalkthroughContainer stepCount={5} activeStep={step} onRewind={rewind}>
			{/* Step 1: App info home, pointer on Permissions row */}
			<WalkthroughFrame visible={step === 0}>
				<AndroidNavBar title="App info" />
				<Stack gap={2} align="center" py={2}>
					<VCGLogo size={24} />
					<Text fz={11} fw={500} ta="center">
						Village Caregiving
					</Text>
				</Stack>
				<AndroidSettingsRow label="Notifications" subtitle="On" />
				<AndroidSettingsRow
					label="Permissions"
					subtitle="Location"
					highlighted
				/>
				<AndroidSettingsRow label="Storage & cache" subtitle="35.77 MB used" />
				<TapPointer top={118} right={30} />
			</WalkthroughFrame>

			{/* Step 2: Permissions screen, pointer on Microphone */}
			<WalkthroughFrame visible={step === 1}>
				<AndroidNavBar title="App permissions" />
				<Stack gap={2} align="center" py={2}>
					<VCGLogo size={20} />
				</Stack>
				<Text fz={9} c="dimmed" px="sm" pb={1}>
					Not allowed
				</Text>
				<AndroidSettingsRow
					icon={
						<IconMicrophone size={13} color="var(--mantine-color-dimmed)" />
					}
					label="Microphone"
					highlighted
				/>
				<Text fz={9} c="dimmed" px="sm" pt={4} pb={1}>
					Allowed
				</Text>
				<AndroidSettingsRow
					icon={<IconMapPin size={13} color="var(--mantine-color-dimmed)" />}
					label="Location"
				/>
				<TapPointer top={96} right={30} />
			</WalkthroughFrame>

			{/* Step 3: Microphone permission, "Don't allow" selected, pointer on "Allow only while using" */}
			<WalkthroughFrame visible={step === 2}>
				<AndroidNavBar title="Microphone permission" />
				<Text fz={9} c="dimmed" px="sm" pt={2} pb={1}>
					Microphone access for this app
				</Text>
				<AndroidRadioRow
					label="Allow only while using the app"
					selected={false}
					highlighted
				/>
				<AndroidRadioRow label="Ask every time" selected={false} />
				<AndroidRadioRow label="Don't allow" selected={true} />
				<TapPointer top={62} right={30} />
			</WalkthroughFrame>

			{/* Step 4: "Allow only while using" now selected */}
			<WalkthroughFrame visible={step === 3}>
				<AndroidNavBar title="Microphone permission" />
				<Text fz={9} c="dimmed" px="sm" pt={2} pb={1}>
					Microphone access for this app
				</Text>
				<AndroidRadioRow
					label="Allow only while using the app"
					selected={true}
					highlighted
				/>
				<AndroidRadioRow label="Ask every time" selected={false} />
				<AndroidRadioRow label="Don't allow" selected={false} />
			</WalkthroughFrame>

			{/* Step 5: Tap Open Settings below */}
			<WalkthroughFrame visible={step === 4}>
				<TapOpenSettingsStep />
			</WalkthroughFrame>
		</WalkthroughContainer>
	);
}
